"use client";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

export type SectionNavLink = { id: string; label: string };

/**
 * The port of das-section-nav.js: a strip of jump links that sticks under the
 * header once the page scrolls past its resting place, and marks the section
 * the page is sitting in.
 *
 * Sections are matched on data-id rather than id, so the jump is done by hand
 * and lands clear of the header and the bar itself.
 *
 * --nav-top and --nav-height are written by measure(): the first is the
 * header's sticky height, the second is what the section holds on to once the
 * bar leaves the flow, so nothing below it jumps. The background lives on the
 * inner bar rather than the section for the same reason: once stuck, the
 * section is an empty spacer. It matches the header's blurred white so the two
 * read as one bar when they meet.
 */
export default function SectionNav({ links }: { links: SectionNavLink[] }) {
  const navRef = useRef<HTMLElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const metrics = useRef({ topSpacing: 0, restingTop: 0 });
  const [stuck, setStuck] = useState(false);
  const [current, setCurrent] = useState(links[0]?.id);

  const target = (id: string) =>
    document.querySelector<HTMLElement>(`[data-id="${id}"]`);

  const offsetFor = (element: HTMLElement) => {
    const inner = innerRef.current?.offsetHeight ?? 0;

    return Math.max(
      0,
      element.getBoundingClientRect().top +
        window.scrollY -
        metrics.current.topSpacing -
        inner,
    );
  };

  useEffect(() => {
    const nav = navRef.current;
    const inner = innerRef.current;

    if (!nav || !inner) {
      return;
    }

    const header = document.getElementById("site-header");

    const headerMetric = (name: string) =>
      header
        ? parseInt(getComputedStyle(header).getPropertyValue(name), 10) || 0
        : 0;

    // Measured with the bar out of its stuck state, or it measures the fixed bar.
    const measure = () => {
      const wasStuck = nav.dataset.stuck;

      nav.dataset.stuck = "false";

      metrics.current.topSpacing = headerMetric("--header-sticky-height");
      metrics.current.restingTop =
        nav.getBoundingClientRect().top + window.scrollY;

      nav.style.setProperty("--nav-top", `${metrics.current.topSpacing}px`);
      nav.style.setProperty("--nav-height", `${inner.offsetHeight}px`);

      nav.dataset.stuck = wasStuck ?? "false";
    };

    // A section counts as current once its top has passed under the bar, so
    // the last one to do so wins.
    const refresh = () => {
      const scrollTop = window.scrollY;
      let active = links[0]?.id;

      setStuck(
        scrollTop >= metrics.current.restingTop - metrics.current.topSpacing,
      );

      links.forEach((link) => {
        const element = target(link.id);

        if (element && scrollTop >= offsetFor(element) - 2) {
          active = link.id;
        }
      });

      setCurrent(active);
    };

    const onResize = () => {
      measure();
      refresh();
    };

    measure();
    refresh();

    // The hero above sizes off svh, so measure again once everything has settled.
    window.addEventListener("load", onResize);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", refresh, { passive: true });

    return () => {
      window.removeEventListener("load", onResize);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", refresh);
    };
  }, [links]);

  const jump = (id: string) => {
    const element = target(id);

    if (element) {
      window.scrollTo({ top: offsetFor(element), behavior: "smooth" });
    }
  };

  return (
    <section
      ref={navRef}
      data-stuck={stuck}
      className="group/section-nav relative z-4 js-das-section-nav data-[stuck=true]:h-(--nav-height)"
    >
      <div
        ref={innerRef}
        className="w-full border-b border-das-hairline bg-white/80 backdrop-blur-[20px] group-data-[stuck=true]/section-nav:fixed group-data-[stuck=true]/section-nav:top-(--nav-top) group-data-[stuck=true]/section-nav:right-0 group-data-[stuck=true]/section-nav:left-0 group-data-[stuck=true]/section-nav:z-4"
      >
        <div className="shell shell-m">
          {/* Too many labels to fit on a phone, so there the row scrolls instead of wrapping. */}
          <ul className="m-0 flex list-none items-center justify-center p-0 mobile:justify-start mobile:overflow-x-auto mobile:[-ms-overflow-style:none] mobile:[scrollbar-width:none] mobile:[&::-webkit-scrollbar]:hidden">
            {links.map((link) => {
              const active = link.id === current;

              return (
                <li
                  key={link.id}
                  className="m-0 not-first:ml-1 mobile:shrink-0"
                >
                  {/*
                   * The buttons carry the row's height, so the active underline lands on the bar's bottom
                   * edge. Every button has the 2px underline, so gaining the active state never shifts the row.
                   */}
                  <button
                    type="button"
                    className={clsx(
                      "block cursor-pointer appearance-none border-x-0 border-t-0 border-b-2 bg-transparent px-4 pt-4 pb-3.5 font-das text-[14px] leading-[1.2] whitespace-nowrap transition-[color,border-color] duration-400 ease-[ease] mobile:px-3 mobile:pt-[15px] mobile:pb-[13px] mobile:text-[13px]",
                      active
                        ? "border-b-das-green font-bold text-brand-blue hover:text-brand-blue"
                        : "border-b-transparent font-medium text-das-gray hover:text-das-black",
                    )}
                    data-target={link.id}
                    onClick={() => jump(link.id)}
                  >
                    {link.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
